import { InboxOutlined } from '@ant-design/icons';
import { Button, notification, Typography, Upload } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal from '../../foundation/components/modal/Modal';
import { useModalForm } from '../../foundation/hooks';
import { useUploadVcfMutation } from '../api/variantApi';
import { VCF_UPLOAD_NOTIFICATION_MESSAGES } from './constants';

const VcfUploadPage = () => {
  const navigate = useNavigate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadVcf] = useUploadVcfMutation();

  const closeModal = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const { isLoading, setIsLoading, createModalOpts } = useModalForm({
    onClose: closeModal,
    requireWorkflowEdit: false,
  });

  const handleBeforeUpload: UploadProps['beforeUpload'] = useCallback(
    (file: RcFile) => {
      setSelectedFile(file);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          originFileObj: file,
        },
      ]);
      return Upload.LIST_IGNORE;
    },
    [],
  );

  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    setFileList([]);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_FAILED,
        description: VCF_UPLOAD_NOTIFICATION_MESSAGES.FILE_REQUIRED,
      });
      return;
    }

    const formData = new FormData();
    formData.append('vcf_file', selectedFile, selectedFile.name);

    try {
      setIsLoading(true);
      const response = await uploadVcf(formData).unwrap();
      notification.success({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_STARTED,
        description: response.message,
      });
      navigate('/running-tasks');
    } catch {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_FAILED,
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedFile, setIsLoading, uploadVcf]);

  return (
    <>
      <Modal
        {...createModalOpts('Upload variants (VCF)')}
        className="vcf-upload-modal"
        confirmLoading={isLoading}
        footer={null}
      >
        <Typography.Paragraph>
          Select a VCF file to create its variants in independent background
          jobs.
        </Typography.Paragraph>
        <Upload.Dragger
          accept=".vcf,.vcf.gz"
          beforeUpload={handleBeforeUpload}
          disabled={isLoading}
          fileList={fileList}
          maxCount={1}
          onRemove={handleRemove}
          showUploadList={{ showRemoveIcon: !isLoading }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag a VCF file here</p>
          <p className="ant-upload-hint">
            Only .vcf and .vcf.gz files are accepted.
          </p>
        </Upload.Dragger>
        <div className="vcf-upload-modal__actions">
          <Button onClick={closeModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedFile || isLoading}
            loading={isLoading}
          >
            Confirm upload
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default VcfUploadPage;
